import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Button, Table, Modal, Badge, Tabs, Tab, Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FaEye, FaMapMarkerAlt, FaCheck, FaTimes, FaClock } from "react-icons/fa";
import { GetEmployeeDailyAttList } from "../utils/queries";
import { convertDateFormat } from "../utils/helper";

const StaffAttendanceListScreen = () => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [staffList, setStaffList] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [reviewRemark, setReviewRemark] = useState("");

  const { userInfo } = useSelector((state) => state.auth);
  const ReqCompanyCode = userInfo && userInfo[13]?.InfoField;

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await GetEmployeeDailyAttList(convertDateFormat(selectedDate), "", ReqCompanyCode);
      const action = response[0]?.ActionType;
      if (action > 0) {
        const JSONData1 = JSON.parse(response[0]?.JSONData1);
        setStaffList(JSONData1);
      } else {
        toast.warn(response[0]?.ErrorMessage || "Failed to load data");
      }
    } catch (error) {
      console.error("Error fetching attendance:", error);
      toast.error("Failed to fetch attendance data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedDate]);

  const filteredStaffList = staffList.filter(staff => 
    staff.RangeType?.toLowerCase() === "outdoor" &&
    staff.reviewsts?.toLowerCase() === activeTab
  );

  const openMapWithLatLng = (latlng) => {
    if (!latlng) return;
    const [latitude, longitude] = latlng.split(',').map(coord => coord.trim());
    const url = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
    window.open(url, '_blank');
  };

  const handleReview = async () => {
    if (!reviewRemark.trim()) {
      toast.error("Please enter remarks");
      return;
    }

    try {
      // Call your update API here
      // await updateReview(selectedMember.AttID, selectedStatus, reviewRemark);
      toast.success("Review updated successfully");
      setShowModal(false);
      fetchData(); // Refresh the list
    } catch (error) {
      toast.error("Failed to update review");
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return <FaClock className="text-warning" />;
      case "approved":
        return <FaCheck className="text-success" />;
      case "rejected":
        return <FaTimes className="text-danger" />;
      default:
        return <FaClock className="text-warning" />;
    }
  };

  return (
    <Container className="py-4">
      <Row className="mb-3 align-items-center justify-content-between">
        <Col xs={12} md={6}>
          <h4 className="text-center">Outdoor Attendance</h4>
        </Col>
        <Col xs={12} md="auto" className="d-flex gap-2 mt-2 mt-md-0">
          <Form.Control 
            type="date" 
            value={selectedDate} 
            onChange={(e) => setSelectedDate(e.target.value)} 
          />
          <Button variant="primary" onClick={fetchData}>
            Refresh
          </Button>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : staffList.length === 0 ? (
        <div className="text-center py-5">
          <p>No data available</p>
        </div>
      ) : (
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-3"
        >
          <Tab eventKey="pending" title="Pending">
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>View</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Entry Date</th>
                    <th>Entry Time</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStaffList.map((staff) => (
                    <tr key={staff.AttID}>
                      <td>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => {
                            setSelectedMember(staff);
                            setSelectedStatus(staff.reviewsts || "pending");
                            setReviewRemark(staff.reviewRemarks || "");
                            setShowModal(true);
                          }}
                        >
                          <FaEye />
                        </Button>
                      </td>
                      <td>{staff.EmployeeName}</td>
                      <td>{staff.AttType}</td>
                      <td>{new Date(staff.AttDateTime).toLocaleDateString()}</td>
                      <td>{new Date(staff.AttDateTime).toLocaleTimeString()}</td>
                      <td>
                        <Badge bg="warning">
                          {getStatusIcon(staff.reviewsts)} Pending
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Tab>
          <Tab eventKey="approved" title="Approved">
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>View</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Entry Date</th>
                    <th>Entry Time</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStaffList.map((staff) => (
                    <tr key={staff.AttID}>
                      <td>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => {
                            setSelectedMember(staff);
                            setSelectedStatus(staff.reviewsts || "approved");
                            setReviewRemark(staff.reviewRemarks || "");
                            setShowModal(true);
                          }}
                        >
                          <FaEye />
                        </Button>
                      </td>
                      <td>{staff.EmployeeName}</td>
                      <td>{staff.AttType}</td>
                      <td>{new Date(staff.AttDateTime).toLocaleDateString()}</td>
                      <td>{new Date(staff.AttDateTime).toLocaleTimeString()}</td>
                      <td>
                        <Badge bg="success">
                          {getStatusIcon(staff.reviewsts)} Approved
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Tab>
          <Tab eventKey="rejected" title="Rejected">
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>View</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Entry Date</th>
                    <th>Entry Time</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStaffList.map((staff) => (
                    <tr key={staff.AttID}>
                      <td>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => {
                            setSelectedMember(staff);
                            setSelectedStatus(staff.reviewsts || "rejected");
                            setReviewRemark(staff.reviewRemarks || "");
                            setShowModal(true);
                          }}
                        >
                          <FaEye />
                        </Button>
                      </td>
                      <td>{staff.EmployeeName}</td>
                      <td>{staff.AttType}</td>
                      <td>{new Date(staff.AttDateTime).toLocaleDateString()}</td>
                      <td>{new Date(staff.AttDateTime).toLocaleTimeString()}</td>
                      <td>
                        <Badge bg="danger">
                          {getStatusIcon(staff.reviewsts)} Rejected
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Tab>
        </Tabs>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Attendance Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedMember && (
            <div>
              <Row>
                <Col md={6}>
                  <h5>Employee Information</h5>
                  <p><strong>Name:</strong> {selectedMember.EmployeeName}</p>
                  <p><strong>ID:</strong> {selectedMember.EMPCODE}</p>
                  <p><strong>Type:</strong> {selectedMember.AttType}</p>
                  <p><strong>Entry Date:</strong> {new Date(selectedMember.AttDateTime).toLocaleDateString()}</p>
                  <p><strong>Entry Time:</strong> {new Date(selectedMember.AttDateTime).toLocaleTimeString()}</p>
                  {selectedMember.AttLocation && (
                    <p>
                      <strong>Location:</strong>{" "}
                      <Button
                        variant="link"
                        className="p-0"
                        onClick={() => openMapWithLatLng(selectedMember.AttLocation)}
                      >
                        <FaMapMarkerAlt className="text-danger me-1" />
                        View on Map
                      </Button>
                    </p>
                  )}
                </Col>
                <Col md={6}>
                  {selectedMember.AttImage && (
                    <img
                      src={`${process.env.REACT_APP_API_URL}${selectedMember.AttImage}`}
                      alt="Attendance"
                      className="img-fluid rounded"
                    />
                  )}
                </Col>
              </Row>
              <hr />
              <Row>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Status</strong></Form.Label>
                    <Form.Select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                    >
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Remarks</strong></Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={reviewRemark}
                      onChange={(e) => setReviewRemark(e.target.value)}
                      placeholder="Enter remarks here..."
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleReview}>
            Update
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default StaffAttendanceListScreen; 